import { HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IHttpResponse, IHttpStatus } from '@partners/api';
import { generateGuid, Guid, isFunction } from '@partners/utils';
import { Observable, throwError } from 'rxjs';
import {
  IError,
  IErrorsConfig,
  ILocalError,
  ILocalErrorsConfig,
  IRemoteError,
  IRemoteErrorsConfig,
  ISeverityHandler
} from './errors.interface';
import { TErrorOrigin, TErrorOutput, TSeverityCallback, TSeverityCode, TSeveritySymbol } from './errors.types';

export abstract class BaseError extends Error implements IError {
  title: string;
  origin: TErrorOrigin;
  output: TErrorOutput;
  abort: boolean;
  code: string | number;
  token: Guid;

  constructor({ token, title, message, stack, output, abort }: IError) {
    super();
    this.token = token || generateGuid();
    this.title = title;
    this.message = message;
    this.stack = stack;
    this.output = output;
    this.abort = abort;
  }
}

export class RemoteError extends BaseError implements IRemoteError {
  request?: HttpRequest<unknown>;
  response: IHttpResponse<unknown>;
  severity?: Severity;

  get severityCode(): TSeverityCode {
    return this.severity.code as TSeverityCode;
  }

  get safeStatus(): IHttpStatus {
    return this.response?.status;
  }

  constructor(error: IRemoteError) {
    super(error);

    this.origin = 'remote';
    this.output = error.output || 'dialog';
    this.response = error.response;
    this.request = error?.request;
    
    const { headlineMessage, messageCode, message } = this.safeStatus;
    this.title = headlineMessage;
    this.message = message;
    this.severity = new Severity(messageCode as TSeverityCode);
  }
}

export class LocalError extends BaseError {
  constructor(error: ILocalError) {
    super(error);
    this.origin = 'local';
    this.output = error.output || 'console';
  }
}

export class Severity {
  readonly code: TSeverityCode;

  constructor(private severityCode: TSeverityCode) {
    this.code = severityCode;
  }

  isCodeOfType(char: TSeveritySymbol): boolean {
    return this.code.startsWith(char);
  }

  // replace code to "_*"" for handling any codes number
  toGenericCodeType(code: TSeverityCode): TSeverityCode {
    return `${code.charAt(0)}_*` as TSeverityCode;
  }

  isWarning(): boolean {
    return this.isCodeOfType('W');
  }
  isError(): boolean {
    return this.isCodeOfType('E');
  }
  isFatal(): boolean {
    return this.isCodeOfType('F');
  }
  isInformation(): boolean {
    return this.isCodeOfType('I');
  }
  isSuccess(): boolean {
    return this.isCodeOfType('S');
  }
  isUndefined(): boolean {
    return !this.isWarning() && !this.isError() && !this.isInformation() && !this.isFatal() && !this.isSuccess();
  }
}

@Injectable({
  providedIn: 'root'
})
export abstract class RootErrorsConfig implements IErrorsConfig {
  abstract localErrors: ILocalErrorsConfig;
  abstract remoteErrors: IRemoteErrorsConfig;

  constructor() {}
}

@Injectable()
export abstract class SeverityHandler implements ISeverityHandler {
  abstract handlers: Map<TSeverityCode, TSeverityCallback>;
  constructor() {}
  // abstract readonly handlers: Map<TSeverityCode, TSeverityHandlerFn>;

  inspect<T>(remoteError: IRemoteError): Observable<never> {
    if (!remoteError?.severity) {
      /** error is valid, but not of a recognized type. this should not happen its just for safety */
      return throwError(() => remoteError);
    }

    const { severity } = remoteError;
    const { code } = severity;

    /** for specific error like W_0002 */
    /** try find a custom handler function for the severity */
    let handler = this.handlers.get(code);
    if (isFunction(handler)) {
      /**
       * if we got to this point we know that a custom error handler was provided for this error, activate the
       * custom error handler to handle this error
       */
      return handler.call(null, remoteError) as Observable<never>;
    }

    /** for general error like F_*, E_*, W_*  who has custom handler(like simulator) */
    handler = this.handlers.get(severity.toGenericCodeType(code));
    if (isFunction(handler)) {
      return handler.call(null, remoteError) as Observable<never>;
    }

    /** no custom handler was provided for this error, rethrow it to let the default handler pick it up */
    return throwError(() => remoteError);
  }
}
function TSeverityOverride(TSeverityOverride: any) {
  throw new Error('Function not implemented.');
}
